import React from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { isStandalone, pushSupported, permiso, isIOS, isAndroid, estadoServidor, activarAvisos } from "@/lib/push";
import { hayDialogoInstalacion, onCambioInstalacion, lanzarInstalacion, instaladaEnEsteEquipo, comprobarInstalada, enviarDiagnostico } from "@/lib/instalar";
import { PasosPC } from "@/components/intro/InstalarMovil";

// Perfil › Avisos en el móvil: estado y acceso a la guía.
export const PushPrefs = () => {
  const navigate = useNavigate();
  return (
    <div className="bg-white border border-[#E5DFD5] rounded-2xl p-6 shadow-sm" data-testid="push-prefs">
      <h2 className="font-heading text-lg font-bold text-[#1C2421] flex items-center gap-2"><Icons.Smartphone size={20} className="text-[#3A6B56]" /> Avisos en el móvil</h2>
      <p className="text-sm text-[#6E7A74] mt-1">
        {pushSupported() && permiso() === "granted" ? "Notificaciones permitidas en este dispositivo." : "Este dispositivo aún no tiene los avisos activados."}
        {!isStandalone() && " "}
      </p>
      <button data-testid="push-prefs-guia" onClick={() => navigate("/avisos-movil")}
        className="mt-4 w-full min-h-[52px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base flex items-center justify-center gap-2">
        <Icons.ClipboardCheck size={20} /> Instalar y comprobar avisos
      </button>
    </div>
  );
};

// Inicio: tarjeta discreta mientras falte instalar, permitir o registrar el dispositivo.
const necesitaInstalar = () => (isIOS() || isAndroid()) && !isStandalone();
const esMovil = () => isIOS() || isAndroid();
const SEMANA = 7 * 24 * 3600 * 1000;
const pospuesta = () => Date.now() - Number(localStorage.getItem("aldia_push_card_tarde") || 0) < SEMANA;
const posponer = () => localStorage.setItem("aldia_push_card_tarde", String(Date.now()));

const Tarjeta = ({ testid, icon: Icon, titulo, texto, children }) => (
  <div data-testid={testid} className="bg-white border border-[#E5DFD5] rounded-2xl p-4 flex gap-3 items-start">
    <span className="w-10 h-10 rounded-xl bg-[#E8F0EC] flex items-center justify-center shrink-0"><Icon size={20} className="text-[#3A6B56]" /></span>
    <div className="flex-1">
      <p className="text-base font-semibold text-[#1C2421]">{titulo}</p>
      {texto && <p className="text-sm text-[#6E7A74] mt-0.5">{texto}</p>}
      <div className="flex flex-wrap gap-2 mt-3">{children}</div>
    </div>
  </div>
);

// Ordenador: instalación (si el navegador la ofrece) y avisos del navegador, por separado.
const InstalarPC = () => {
  const [disponible, setDisponible] = React.useState(hayDialogoInstalacion);
  const [instalada, setInstalada] = React.useState(() => isStandalone() || instaladaEnEsteEquipo());
  const [ayuda, setAyuda] = React.useState(null); // null | "menu" | "error"
  const [oculta, setOculta] = React.useState(() => Date.now() - Number(localStorage.getItem("aldia_instalar_pc_tarde") || 0) < SEMANA);
  React.useEffect(() => onCambioInstalacion(() => { setDisponible(hayDialogoInstalacion()); setInstalada(isStandalone() || instaladaEnEsteEquipo()); }), []);
  React.useEffect(() => { comprobarInstalada().then((v) => v && setInstalada(true)); }, []);
  if (instalada || oculta) return null;
  const instalar = async () => {
    const r = await lanzarInstalacion();
    enviarDiagnostico("instalar", r);
    if (r === "aceptada") { toast.success("AL DÍA Fácil se está instalando en este ordenador."); setAyuda(null); }
    else setAyuda(r === "error" ? "error" : "menu");
  };
  const masTarde = () => { localStorage.setItem("aldia_instalar_pc_tarde", String(Date.now())); setOculta(true); };
  const conBoton = disponible && !ayuda;
  return (
    <Tarjeta testid="pc-instalar" icon={Icons.Monitor} titulo="Instalar AL DÍA Fácil en este ordenador"
      texto={ayuda === "error" ? "No se ha podido iniciar la instalación desde aquí. Sigue estos pasos:" : ayuda === "menu" ? "Puedes instalarla siguiendo estos pasos:" : "Tendrás un acceso directo para abrirla como cualquier otra aplicación."}>
      {conBoton && <button data-testid="pc-instalar-btn" onClick={instalar} className="min-h-[44px] px-4 rounded-xl bg-[#3A6B56] text-white font-semibold">Instalar</button>}
      {!conBoton && !ayuda && <button data-testid="pc-instalar-ver-pasos" onClick={() => { setAyuda("menu"); enviarDiagnostico("ver-pasos"); }} className="min-h-[44px] px-4 rounded-xl bg-[#3A6B56] text-white font-semibold">Ver cómo instalar</button>}
      {ayuda && <div className="w-full mt-1" data-testid="pc-instalar-pasos"><PasosPC /></div>}
      <button data-testid="pc-instalar-mas-tarde" onClick={masTarde} className="min-h-[44px] px-4 rounded-xl text-[#4A5550] font-semibold">{ayuda ? "Entendido" : "Más tarde"}</button>
      <p className="w-full text-xs text-[#6E7A74]" data-testid="pc-instalar-aviso-informe">Al pulsar «Instalar» o «Ver cómo instalar» se envía a AL DÍA un informe técnico anónimo (navegador, sistema y resultado) para resolver problemas de instalación. No incluye datos personales y se borra a los 30 días.</p>
    </Tarjeta>
  );
};

const AvisosPC = () => {
  const [estado, setEstado] = React.useState(null); // null | "activar" | "no-disponible"
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    if (pospuesta()) return;
    if (!pushSupported() || permiso() === "denied") { setEstado("no-disponible"); return; }
    if (permiso() !== "granted") { setEstado("activar"); return; }
    estadoServidor().then((r) => setEstado(r?.registrada ? null : "activar")).catch(() => setEstado("activar"));
  }, []);
  if (!estado) return null;
  if (estado === "no-disponible") return (
    <Tarjeta testid="pc-avisos-no-disponible" icon={Icons.BellOff} titulo="Los avisos no están disponibles en este navegador. Puedes seguir recibiéndolos en tu móvil.">
      <button data-testid="pc-avisos-entendido" onClick={() => { posponer(); setEstado(null); }} className="min-h-[44px] px-4 rounded-xl text-[#4A5550] font-semibold">Entendido</button>
    </Tarjeta>
  );
  const activar = async () => {
    setBusy(true);
    try {
      const r = await activarAvisos();
      if (r.ok) { toast.success("Avisos activados en este ordenador."); setEstado(null); } else setEstado("no-disponible");
    } catch { setEstado("no-disponible"); }
    finally { setBusy(false); }
  };
  return (
    <Tarjeta testid="push-banner" icon={Icons.BellRing} titulo="Activa los avisos en este ordenador" texto="Puedes recibir avisos en este navegador si lo permite tu equipo.">
      <button data-testid="pc-avisos-activar" disabled={busy} onClick={activar} className="min-h-[44px] px-4 rounded-xl bg-[#3A6B56] text-white font-semibold disabled:opacity-60">{busy ? "Activando…" : "Activar avisos"}</button>
    </Tarjeta>
  );
};

export const PushBanner = () => (esMovil() ? <PushBannerMovil /> : <><InstalarPC /><AvisosPC /></>);

const PushBannerMovil = () => {
  const navigate = useNavigate();
  const [mostrar, setMostrar] = React.useState(false);
  React.useEffect(() => {
    const pospuesta = Number(localStorage.getItem("aldia_push_card_tarde") || 0);
    if (Date.now() - pospuesta < 7 * 24 * 3600 * 1000) return;
    if (necesitaInstalar()) { setMostrar(true); return; }
    if (!pushSupported()) return;
    if (permiso() !== "granted") { setMostrar(true); return; }
    estadoServidor().then((r) => setMostrar(!r?.registrada)).catch(() => setMostrar(true));
  }, []);
  if (!mostrar) return null;
  const masTarde = () => { localStorage.setItem("aldia_push_card_tarde", String(Date.now())); setMostrar(false); };
  return (
    <div data-testid="push-banner" className="bg-white border border-[#E5DFD5] rounded-2xl p-4 flex gap-3 items-start">
      <span className="w-10 h-10 rounded-xl bg-[#E8F0EC] flex items-center justify-center shrink-0"><Icons.BellRing size={20} className="text-[#3A6B56]" /></span>
      <div className="flex-1">
        <p className="text-base font-semibold text-[#1C2421]">Instala AL DÍA Fácil y activa los avisos</p>
        <div className="flex gap-2 mt-3">
          <button data-testid="push-banner-activar" onClick={() => navigate("/avisos-movil")} className="min-h-[44px] px-4 rounded-xl bg-[#3A6B56] text-white font-semibold">Configurar ahora</button>
          <button data-testid="push-banner-cerrar" onClick={masTarde} className="min-h-[44px] px-4 rounded-xl text-[#4A5550] font-semibold">Más tarde</button>
        </div>
      </div>
    </div>
  );
};
